import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck'
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark'
import { Button } from '@blueprintjs/core/lib/cjs/components/button/buttons'
import { Icon } from '@blueprintjs/core/lib/cjs/components/icon/icon'
import { Spinner } from '@blueprintjs/core/lib/cjs/components/spinner/spinner'
import { push } from 'connected-react-router'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import * as paths from 'src/constants/paths'
import TemplateActionsList from 'src/routes/Runbooks/RunbooksTemplates/TemplateActionList'
import {
    getRunbookTemplateFromServer,
    getRunbookTemplateActionsFromServer,
    saveRunbookTemplateActions,
    createTemplateActionOnServer,
    deleteTemplateActionFromServer,
    renameRunbookTemplateActions,
} from 'src/store/modules/runbooks'
import {
    getTemplateTypeLabel,
    getOrganizationLabel,
    getRunbookCategoryLabel,
    getDeviceTypeLabel,
} from 'src/routes/Runbooks/RunbooksTemplates/utils'
import {
    flattenActionsForPersistence,
    getActionsSignature,
    normalizeActionRecords,
} from './utils'

import pageStyles from './templateActionListPage.module.scss'

const ACTIONS_PER_PAGE = 1000

/**
 * Page component for viewing and reordering actions on a runbook template.
 * Template metadata is fetched through Redux, while local action edits are
 * tracked in component state until explicitly saved.
 *
 * @param {object} props Component props.
 * @param {object} props.match Router match object.
 * @param {object|null} props.template Selected template details.
 * @param {Array<object>} props.templateActions Template actions from the store.
 * @param {boolean} props.isLoadingTemplate Loading state for template details.
 * @param {boolean} props.isLoadingActions Loading state for template actions.
 * @param {Function} props.navigateToTemplatesList Navigate back to list page.
 * @param {Function} props.getRunbookTemplateFromServer Fetch template details.
 * @param {Function} props.getRunbookTemplateActionsFromServer Fetch template actions.
 * @returns {JSX.Element}
 */
const TemplateActionListPage = ({
    match,
    template,
    templateActions,
    isLoadingTemplate,
    isLoadingActions,
    isSavingTemplate,
    navigateToTemplatesList,
    getRunbookTemplateFromServer,
    getRunbookTemplateActionsFromServer,
    saveRunbookTemplateActions,
    createTemplateAction,
    deleteTemplateAction,
    renameRunbookTemplateActions,
}) => {
    const templateId = Number(match?.params?.templateId)
    const [localTemplateActions, setLocalTemplateActions] = useState([])
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [expandResetVersion, setExpandResetVersion] = useState(0)
    const [queuedDeletedActionIds, setQueuedDeletedActionIds] = useState([])
    const initialActionsSignatureRef = useRef('[]')

    useEffect(() => {
        if (!Number.isFinite(templateId)) {
            return
        }

        getRunbookTemplateFromServer(templateId)
        getRunbookTemplateActionsFromServer({
            templateId,
            page: 1,
            per_page: ACTIONS_PER_PAGE,
        })
    }, [
        getRunbookTemplateActionsFromServer,
        getRunbookTemplateFromServer,
        templateId,
    ])

    const normalizedServerActions = useMemo(
        () => normalizeActionRecords(templateActions),
        [templateActions]
    )

    useEffect(() => {
        setLocalTemplateActions(normalizedServerActions)
        initialActionsSignatureRef.current = getActionsSignature(
            normalizedServerActions
        )
        setHasUnsavedChanges(false)
        setExpandResetVersion((currentVersion) => currentVersion + 1)
    }, [normalizedServerActions])

    const handleTemplateActionsChange = useCallback((nextActions) => {
        setLocalTemplateActions(nextActions)
        setHasUnsavedChanges(
            getActionsSignature(nextActions) !==
                initialActionsSignatureRef.current
        )
    }, [])

    const handleSaveTemplate = useCallback(async () => {
        if (
            !Number.isFinite(templateId) ||
            !hasUnsavedChanges ||
            isSavingTemplate
        ) {
            return
        }

        try {
            const initialItems = JSON.parse(initialActionsSignatureRef.current)
            const initialItemsById = new Map(
                initialItems.map((item) => [item.id, item])
            )
            const currentItems = flattenActionsForPersistence(
                localTemplateActions
            )
            const changedItems = currentItems.filter((item) => {
                const previousItem = initialItemsById.get(item.id)
                if (!previousItem) {
                    return true
                }

                return (
                    previousItem.parent !== item.parent ||
                    Number(previousItem.sequence_id) !==
                        Number(item.sequence_id)
                )
            })

            const currentItemIds = new Set(currentItems.map((item) => item.id))
            const deletedActionIds = Array.from(
                new Set([
                    ...queuedDeletedActionIds,
                    ...initialItems
                        .filter((item) => !currentItemIds.has(item.id))
                        .map((item) => item.id),
                ])
            )

            // Separate new actions (with negative IDs) from existing ones
            const newActions = changedItems.filter((item) => item.id < 0)
            const existingChangedItems = changedItems.filter((item) => item.id > 0)

            const renamedItems = currentItems.filter((item) => {
                const previousItem = initialItemsById.get(item.id)
                if (!previousItem) {
                    return false
                }

                return previousItem.name !== item.name
            })

            if (deletedActionIds.length) {
                await deleteTemplateAction({
                    templateId,
                    actionIds: deletedActionIds,
                    page: 1,
                    per_page: ACTIONS_PER_PAGE,
                    shouldRefetch: false,
                })
            }

            // Create new actions first
            if (newActions.length) {
                await createTemplateAction(templateId, {
                    actions: newActions,
                })
            }

            // Then update existing actions
            if (existingChangedItems.length) {
                await saveRunbookTemplateActions({
                    templateId,
                    actions: existingChangedItems,
                    page: 1,
                    per_page: ACTIONS_PER_PAGE,
                    shouldRefetch: false,
                })
            }

            if (renamedItems.length) {
                await renameRunbookTemplateActions({
                    templateId,
                    actions: renamedItems,
                    page: 1,
                    per_page: ACTIONS_PER_PAGE,
                    shouldRefetch: false,
                })
            }

            if (
                changedItems.length ||
                deletedActionIds.length ||
                renamedItems.length
            ) {
                await getRunbookTemplateActionsFromServer({
                    templateId,
                    page: 1,
                    per_page: ACTIONS_PER_PAGE,
                })
            }

            initialActionsSignatureRef.current = getActionsSignature(
                localTemplateActions
            )

            setHasUnsavedChanges(false)
            setQueuedDeletedActionIds([])
            setExpandResetVersion((currentVersion) => currentVersion + 1)
        } catch (_error) {
            return
        }
    }, [
        getRunbookTemplateActionsFromServer,
        hasUnsavedChanges,
        isSavingTemplate,
        localTemplateActions,
        saveRunbookTemplateActions,
        renameRunbookTemplateActions,
        templateId,
        queuedDeletedActionIds,
        deleteTemplateAction,
        createTemplateAction,
    ])

    const headerValues = useMemo(
        () => ({
            name: template?.name || `Template ${templateId}`,
            templateType: getTemplateTypeLabel(template?.template_type),
            deviceType: getDeviceTypeLabel(template?.device_type),
            runbookCategory: getRunbookCategoryLabel(
                template?.runbook_category
            ),
            organization: getOrganizationLabel(
                template?.organization_scope,
                template?.organization_name,
                template?.organization
            ),
        }),
        [template, templateId]
    )

    const handleQueueDeleteAction = useCallback((actionId) => {
        const numericActionId = Number(actionId)

        if (!Number.isFinite(numericActionId)) {
            return
        }

        setQueuedDeletedActionIds((previousIds) => {
            const nextQueuedIds = previousIds.includes(numericActionId)
                ? previousIds
                : [...previousIds, numericActionId]

            return nextQueuedIds
        })

        setHasUnsavedChanges(true)
    }, [])

    const handleAddActions = useCallback(
        (actionNames) => {
            if (!Number.isFinite(templateId) || !actionNames?.length) {
                return
            }

            const nextNames = actionNames
                .map((actionName) => String(actionName || '').trim())
                .filter(Boolean)

            if (!nextNames.length) {
                return
            }

            const nextTopLevelSequence = localTemplateActions.length + 1

            // Generate temporary negative IDs for new actions
            let tempIdCounter = -1
            const newActions = nextNames.map((name, index) => {
                const sequence = nextTopLevelSequence + index

                return {
                    id: tempIdCounter--,
                    name,
                    parent: null,
                    sequence_id: sequence,
                    weight: sequence,
                    children: [],
                }
            })

            // Add new actions to local state
            const nextActions = [...localTemplateActions, ...newActions]
            handleTemplateActionsChange(nextActions)
        },
        [
            templateId,
            handleTemplateActionsChange,
            localTemplateActions,
        ]
    )

    return (
        <div className={pageStyles.wrapper}>
            <div
                className={cx(
                    pageStyles.listWrapper,
                    pageStyles.templateDetailsPage
                )}
            >
                <div className={pageStyles.templateActionListPage}>
                    <div className={pageStyles.templateDetailsHeader}>
                        <div className={pageStyles.templateDetailsInfo}>
                            <Icon
                                icon="arrow-left"
                                className={pageStyles.backNavigationButton}
                                onClick={navigateToTemplatesList}
                                iconSize={28}
                            />
                            <div className={pageStyles.templateTitleGroup}>
                                <p className={pageStyles.templateNameTitle}>
                                    {headerValues.name}
                                </p>
                                <div className={pageStyles.templateMetadataRow}>
                                    <span
                                        className={pageStyles.templateTypeBadge}
                                    >
                                        {headerValues.templateType}
                                    </span>
                                    <span
                                        className={pageStyles.templateMetaValue}
                                    >
                                        {headerValues.deviceType}
                                    </span>
                                    <span
                                        className={pageStyles.templateMetaValue}
                                    >
                                        {headerValues.runbookCategory}
                                    </span>
                                    <span
                                        className={pageStyles.templateMetaValue}
                                    >
                                        {headerValues.organization}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={pageStyles.templateDetailsActions}>
                            <Button
                                large
                                onClick={navigateToTemplatesList}
                                icon={<FontAwesomeIcon icon={faCircleXmark} />}
                                className={pageStyles.cancelTemplateButton}
                            >
                                Cancel editing
                            </Button>
                            <Button
                                large
                                icon={<FontAwesomeIcon icon={faCircleCheck} />}
                                className={cx(pageStyles.saveTemplateButton, {
                                    [pageStyles.saveTemplateButtonEnabled]: hasUnsavedChanges,
                                })}
                                onClick={handleSaveTemplate}
                                disabled={
                                    !hasUnsavedChanges ||
                                    isSavingTemplate ||
                                    isLoadingActions
                                }
                            >
                                {isSavingTemplate
                                    ? 'Saving...'
                                    : 'Save template'}
                            </Button>
                        </div>
                    </div>

                    {isLoadingTemplate ? (
                        <div className={pageStyles.templateLoader}>
                            <Spinner size={Spinner.SIZE_STANDARD} />
                        </div>
                    ) : (
                        <TemplateActionsList
                            templateId={templateId}
                            actions={localTemplateActions}
                            isLoading={isLoadingActions}
                            isAddingActions={false}
                            disableAddAction={hasUnsavedChanges}
                            onActionsChange={handleTemplateActionsChange}
                            onAddActions={handleAddActions}
                            resetExpandVersion={expandResetVersion}
                            onQueueDeleteAction={handleQueueDeleteAction}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

TemplateActionListPage.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            templateId: PropTypes.string,
        }),
    }),
    template: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        template_type: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        device_type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        runbook_category: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        organization_scope: PropTypes.string,
        organization_name: PropTypes.string,
        organization: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    templateActions: PropTypes.arrayOf(PropTypes.object),
    isLoadingTemplate: PropTypes.bool,
    isLoadingActions: PropTypes.bool,
    isSavingTemplate: PropTypes.bool,
    navigateToTemplatesList: PropTypes.func.isRequired,
    getRunbookTemplateFromServer: PropTypes.func.isRequired,
    getRunbookTemplateActionsFromServer: PropTypes.func.isRequired,
    saveRunbookTemplateActions: PropTypes.func.isRequired,
    createTemplateAction: PropTypes.func.isRequired,
    deleteTemplateAction: PropTypes.func.isRequired,
    renameRunbookTemplateActions: PropTypes.func.isRequired,
}

TemplateActionListPage.defaultProps = {
    match: null,
    template: null,
    templateActions: [],
    isLoadingTemplate: false,
    isLoadingActions: false,
    isSavingTemplate: false,
}

const mapStateToProps = (state, ownProps) => {
    const templateId = Number(ownProps?.match?.params?.templateId)
    const templatesState = state.runbooks.templates
    const selectedTemplate = templatesState.selectedTemplate
    const isSelectedTemplateMatch =
        selectedTemplate && selectedTemplate.id === templateId

    const template = isSelectedTemplateMatch
        ? selectedTemplate
        : templatesState.byId[templateId] || null

    const templateActions =
        templatesState.selectedTemplateId === templateId
            ? templatesState.selectedTemplateActions
            : []

    return {
        template,
        templateActions,
        isLoadingTemplate:
            templatesState.loadingSelectedTemplate &&
            templatesState.selectedTemplateId === templateId,
        isLoadingActions:
            templatesState.loadingSelectedTemplateActions &&
            templatesState.selectedTemplateId === templateId,
        isSavingTemplate: templatesState.savingSelectedTemplateActions,
    }
}

const mapDispatchToProps = (dispatch) => ({
    navigateToTemplatesList: () => dispatch(push(paths.TEMPLATES_PATH)),
    getRunbookTemplateFromServer: bindActionCreators(
        getRunbookTemplateFromServer,
        dispatch
    ),
    getRunbookTemplateActionsFromServer: bindActionCreators(
        getRunbookTemplateActionsFromServer,
        dispatch
    ),
    saveRunbookTemplateActions: bindActionCreators(
        saveRunbookTemplateActions,
        dispatch
    ),
    createTemplateAction: bindActionCreators(
        createTemplateActionOnServer,
        dispatch
    ),
    deleteTemplateAction: bindActionCreators(
        deleteTemplateActionFromServer,
        dispatch
    ),
    renameRunbookTemplateActions: bindActionCreators(
        renameRunbookTemplateActions,
        dispatch
    ),
})

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TemplateActionListPage)

export { TemplateActionListPage }
